import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  CheckCircle2,
  ClipboardList,
  DollarSign,
  Home,
  Mail,
  PlayCircle,
  Send,
  Users,
} from "lucide-react";
import { CallAPIPostPromise } from "Common/commaon";
import { getDecodeId } from "slices/userProfile/thunk";
import "./agent-dashboard.css";

const emptySummary = {
  stats: {
    totalHomeowners: 0,
    primaryHomeowners: 0,
    sharedHomeowners: 0,
    pendingInvites: 0,
    contractorSuggestions: 0,
    propertySuggestions: 0,
    propertyLeads: 0,
  },
  earnings: {
    earnedAmount: 0,
    earnedCount: 0,
    pendingAmount: 0,
    pendingCount: 0,
  },
  checklist: [],
  recentHomeowners: [],
  recentSuggestions: [],
  recentEarnings: [],
};

const statCards = [
  { key: "totalHomeowners", label: "Homeowners", icon: Users },
  { key: "pendingInvites", label: "Pending invites", icon: Mail },
  { key: "contractorSuggestions", label: "Contractor suggestions", icon: Send },
  { key: "propertyLeads", label: "Property leads", icon: ClipboardList },
];

const tutorialCards = [
  {
    title: "Invite homeowners",
    text: "Send an invite and start building your homeowner network.",
    path: "/apps-real-estate-agent-homeowner",
  },
  {
    title: "Recommend contractors",
    text: "Pick a homeowner, choose contractors, and send trusted suggestions.",
    path: "/apps-real-estate-explore-contractor",
  },
  // {
  //   title: "Manage property leads",
  //   text: "Review buyer and renter inquiries from your listed properties.",
  //   path: "/property-leads",
  // },
];

export default function AgentDashboard() {
  const [summary, setSummary] = useState<any>(emptySummary);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    CallAPIPostPromise("/agent/dashboard-summary", { agentId: getDecodeId() })
      .then((res: any) => {
        setSummary({
          ...emptySummary,
          ...(res || {}),
          stats: {
            ...emptySummary.stats,
            ...(res?.stats || {}),
          },
          earnings: {
            ...emptySummary.earnings,
            ...(res?.earnings || {}),
          },
        });
      })
      .catch(() => {
        setSummary(emptySummary);
      })
      .finally(() => setLoading(false));
  }, []);

  const completedChecklist = useMemo(
    () => summary.checklist.filter((item: any) => item.complete).length,
    [summary.checklist]
  );

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));

  return (
    <div className="tailwind-prefix agent-dashboard">
      <div className="agent-dashboard-hero mb-6 overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
        <div className="grid gap-0 xl:grid-cols-[1.4fr_1fr]">
          <div className="agent-dashboard-hero__intro relative bg-gradient-to-br from-[#17314f] via-[#23527d] to-[#246b73] p-7 text-white">
            {/* <div className="absolute right-6 top-6 rounded-full border border-gray-500 bg-white/10 px-3 py-1 text-xs font-medium text-gray-500">
              Agent earnings
            </div> */}
            <h2 className="mb-3 max-w-2xl text-3xl font-semibold leading-tight">
              Track income from contractor leads you recommend.
            </h2>
            <p className="agent-dashboard-hero__copy max-w-2xl text-sm leading-6">
              Income is counted only after a contractor accepts a homeowner lead that started from your recommendation.
              Pending requests stay separate until that acceptance happens.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/apps-real-estate-agent-homeowner" className="agent-dashboard-action agent-dashboard-action--secondary rounded-lg px-4 py-2 text-sm font-semibold">
                Invite Homeowner
              </Link>
              <Link to="/apps-real-estate-explore-contractor" className="agent-dashboard-action agent-dashboard-action--primary rounded-lg border px-4 py-2 text-sm font-semibold">
                Send Recommendation
              </Link>
            </div>
          </div>

          <div className="grid gap-4 bg-[#f8fafc] p-5 sm:grid-cols-2 xl:grid-cols-1">
            <div className="rounded-xl border border-emerald-100 bg-white p-4 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">Earned income</span>
                <DollarSign className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="text-2xl font-semibold text-gray-900">
                {formatCurrency(summary.earnings.earnedAmount)}
              </div>
              <div className="mt-1 text-xs leading-5 text-gray-500">
                From accepted contractor leads only.
              </div>
            </div>

            <div className="rounded-xl border border-blue-100 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">Lead status</span>
                <ClipboardList className="h-5 w-5 text-blue-600" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-2xl font-semibold text-gray-900">{summary.earnings.earnedCount}</div>
                  <div className="text-xs text-gray-500">Accepted</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-gray-900">{summary.earnings.pendingCount}</div>
                  <div className="text-xs text-gray-500">Waiting</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-gray-500">
                Waiting means homeowner requested the contractor, but the contractor has not accepted yet.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="agent-dashboard-step agent-dashboard-step--recommend rounded-xl border border-emerald-100 bg-emerald-50 p-5">
          <div className="mb-1 text-sm font-semibold text-emerald-900">1. Recommend a contractor</div>
          <div className="text-xs leading-5 text-emerald-800">
            Send a trusted contractor suggestion to a homeowner you can access.
          </div>
        </div>
        <div className="agent-dashboard-step agent-dashboard-step--request rounded-xl border border-blue-100 bg-blue-50 p-5">
          <div className="mb-1 text-sm font-semibold text-blue-900">2. Homeowner sends request</div>
          <div className="text-xs leading-5 text-blue-800">
            When they choose Hire Contractor, RELiBUILD tracks that request back to you.
          </div>
        </div>
        <div className="agent-dashboard-step agent-dashboard-step--accept rounded-xl border border-amber-100 bg-amber-50 p-5">
          <div className="mb-1 text-sm font-semibold text-amber-900">3. Contractor accepts</div>
          <div className="text-xs leading-5 text-amber-800">
            The lead becomes earned income only after contractor acceptance.
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.key} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="rounded-lg bg-blue-50 p-2 text-blue-600">
                  <Icon className="h-5 w-5" />
                </div>
                {loading && <span className="text-xs text-gray-400">Loading</span>}
              </div>
              <div className="text-2xl font-semibold text-gray-900">
                {summary.stats[card.key] || 0}
              </div>
              <div className="mt-1 text-sm text-gray-500">{card.label}</div>
            </div>
          );
        })}
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Earning Activity</h3>
            <p className="text-sm text-gray-500">Only accepted contractor leads are counted as earned income.</p>
          </div>
          <DollarSign className="h-5 w-5 text-emerald-600" />
        </div>
        <div className="divide-y divide-gray-100">
          {summary.recentEarnings.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-500">
              No contractor lead earnings yet.
            </div>
          )}
          {summary.recentEarnings.map((item: any) => {
            const isEarned = item.status === "earned";
            return (
              <div key={`${item.id}-${item.createdAt}`} className="flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900">{item.jobTitle}</div>
                  <div className="text-xs text-gray-500">
                    {item.homeownerName} hired {item.contractorName}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${isEarned ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {isEarned ? "Earned" : "Waiting for contractor"}
                  </span>
                  <span className="min-w-[80px] text-right text-sm font-semibold text-gray-900">
                    {isEarned ? formatCurrency(item.amount) : "$0"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Getting Started</h3>
              <p className="text-sm text-gray-500">
                {completedChecklist} of {summary.checklist.length || 5} steps complete
              </p>
            </div>
            <BookOpen className="h-5 w-5 text-blue-600" />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {summary.checklist.map((item: any) => (
              <Link
                key={item.key}
                to={item.path || "/dashboard"}
                className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 text-decoration-none"
              >
                <CheckCircle2 className={`h-5 w-5 ${item.complete ? "text-green-600" : "text-gray-300"}`} />
                <span className={`text-sm font-medium ${item.complete ? "text-gray-700" : "text-gray-900"}`}>
                  {item.title}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Homeowner Access</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-blue-50 px-4 py-3">
              <span className="text-sm font-medium text-blue-800">Your direct homeowners</span>
              <span className="text-lg font-semibold text-blue-900">{summary.stats.primaryHomeowners}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
              <span className="text-sm font-medium text-gray-700">Additional homeowner access</span>
              <span className="text-lg font-semibold text-gray-900">{summary.stats.sharedHomeowners}</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-amber-50 px-4 py-3">
              <span className="text-sm font-medium text-amber-800">Waiting for acceptance</span>
              <span className="text-lg font-semibold text-amber-900">{summary.stats.pendingInvites}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Recent Suggestions</h3>
          <div className="divide-y divide-gray-100">
            {summary.recentSuggestions.length === 0 && (
              <div className="py-8 text-center text-sm text-gray-500">No suggestions sent yet.</div>
            )}
            {summary.recentSuggestions.map((item: any) => (
              <div key={`${item.type}-${item.id}`} className="flex items-center justify-between gap-4 py-3">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {item.type === "property" ? "Property" : "Contractor"} suggestion to {item.homeownerName}
                  </div>
                  <div className="text-xs text-gray-500">{item.targetName || "Recommendation"}</div>
                </div>
                <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Tutorials</h3>
          <div className="space-y-3">
            {tutorialCards.map((item) => (
              <Link key={item.title} to={item.path} className="block rounded-lg border border-gray-100 p-3 text-decoration-none hover:bg-gray-50">
                <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <PlayCircle className="h-4 w-4 text-blue-600" />
                  {item.title}
                </div>
                <div className="text-xs leading-5 text-gray-500">{item.text}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Recent Homeowners</h3>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {summary.recentHomeowners.length === 0 && (
            <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500">
              Invite a homeowner to start seeing activity here.
            </div>
          )}
          {summary.recentHomeowners.map((item: any) => (
            <div key={`${item.refType}-${item.id}`} className="flex items-center gap-3 rounded-lg border border-gray-100 p-3">
              <div className={`rounded-lg p-2 ${item.refType === "primary" ? "bg-blue-50 text-blue-600" : "bg-gray-50 text-gray-600"}`}>
                <Home className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-gray-900">{item.name}</div>
                <div className="truncate text-xs text-gray-500">{item.email}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
